import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ScrollText as Receipt,
  Plus,
  Search,
  Grid,
  List,
  Clock,
  FileText,
  Loader,
  Trash2,
  Edit,
  Eye,
  Download,
  Send,
  Coins,
  CheckCircle,
  AlertTriangle,
  Filter,
  Building2,
  Calendar,
  Settings,
  Landmark,
  Sparkles
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { formatDate } from '../../utils/formatters';
import '../../styles/invoices.css';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchInvoices,
  fetchClients,
  fetchUsage,
  deleteInvoice,
  markInvoiceSent,
  markInvoicePaid,
  markInvoiceReady,
  markInvoiceConfirmed,
  setStatusFilter,
  setClientFilter
} from '../../store/invoiceSlice';
import { Invoice, InvoiceStatus, InvoiceUsage } from '../../types';
import { downloadAndSavePdf, generateInvoicePdf, downloadAndSaveXml, getInvoiceStatusLabel } from '../../api/invoices';
import { KsefChip } from './ksef/KsefChip';
import KsefSubmitModal from './ksef/KsefSubmitModal';
import { KsefInvoiceState, resolveKsefEnv } from '../../api/ksef';
import { useSubscription } from '../../hooks/useSubscription';
import { formatMoney } from '../../utils/formatters';
import { errorMessage, parseBlobError } from '../../utils/errors';
import { useToast } from '../ui/Toast';
import { useConfirm } from '../ui/ConfirmDialog';

// Status chip — token-driven (light & dark theme aware via globals.css)
const getStatusColor = (status: InvoiceStatus): string => {
  switch (status) {
    case 'paid':             return 'chip chip-paid';
    case 'sent':             return 'chip chip-sent';
    case 'ready_to_process': return 'chip chip-ready';
    case 'confirmed':        return 'chip chip-confirmed';
    case 'overdue':          return 'chip chip-overdue';
    case 'cancelled':        return 'chip chip-cancelled';
    default:                 return 'chip chip-draft';
  }
};

// Usage bar component
const UsageBar: React.FC<{
  label: string;
  used: number;
  limit: number;
  icon: React.ReactNode;
}> = ({ label, used, limit, icon }) => {
  const percentage = Math.min((used / limit) * 100, 100);
  const isAtLimit = used >= limit;

  return (
    <div className="flex items-center gap-3">
      <div className="text-coral-400">{icon}</div>
      <div className="flex-1">
        <div className="flex justify-between text-xs text-foreground-muted mb-1">
          <span>{label}</span>
          <span>{used} / {limit}</span>
        </div>
        <div className="w-full bg-coral-900/30 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${
              isAtLimit ? 'bg-red-500' : percentage > 80 ? 'bg-orange-500' : 'bg-coral-500'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const CreatedInvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { invoices, clients, usage, loading, error, statusFilter, clientFilter } = useAppSelector(
    (state) => state.invoices
  );

  // Get invoiceId from URL params for auto-selection
  const invoiceIdFromUrl = searchParams.get('invoiceId');

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [downloadingXml, setDownloadingXml] = useState<number | null>(null);
  const [ksefModalInvoice, setKsefModalInvoice] = useState<Invoice | null>(null);
  const [ksefStateById, setKsefStateById] = useState<Record<number, KsefInvoiceState>>({});
  const { isKsefEligible } = useSubscription();
  const toast = useToast();
  const confirm = useConfirm();

  const ksefStatusOf = (inv: Invoice) =>
    ksefStateById[inv.id]?.status || (inv as any).ksef?.status || 'none';

  useEffect(() => {
    dispatch(fetchInvoices({}));
    dispatch(fetchClients({}));
    dispatch(fetchUsage());
  }, [dispatch]);

  // Auto-select invoice from URL param
  useEffect(() => {
    if (invoiceIdFromUrl && invoices.length > 0) {
      const invId = parseInt(invoiceIdFromUrl, 10);
      const inv = invoices.find(i => i.id === invId);
      if (inv) {
        // Navigate to the invoice preview
        navigate(`/created-invoices/${invId}`);
      }
    }
  }, [invoiceIdFromUrl, invoices, navigate]);

  // Filter invoices
  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.client?.name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = !statusFilter || invoice.status === statusFilter;
    const matchesClient = !clientFilter || invoice.client_id === clientFilter;

    return matchesSearch && matchesStatus && matchesClient;
  });

  const handleDelete = async (invoiceId: number, e: React.MouseEvent) => {
    e.stopPropagation();

    const invoice = invoices.find((i) => i.id === invoiceId);
    const ok = await confirm({
      title: 'Delete this invoice?',
      message: (
        <>
          <b>{invoice?.invoice_number ?? 'This invoice'}</b> will be permanently removed. This
          cannot be undone.
        </>
      ),
      confirmLabel: 'Delete invoice',
      destructive: true,
    });
    if (!ok) return;

    try {
      setDeleting(invoiceId);
      await dispatch(deleteInvoice(invoiceId)).unwrap();
      toast.success('Invoice deleted.');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to delete the invoice.'));
    } finally {
      setDeleting(null);
    }
  };

  const handleDownload = async (invoice: Invoice, e: React.MouseEvent) => {
    e.stopPropagation();

    if (usage && usage.invoice_downloads >= usage.invoice_downloads_limit) {
      toast.warning('You have reached your PDF download limit. Upgrade your plan to download more.');
      return;
    }

    try {
      setDownloading(invoice.id);
      // Generate PDF if not already generated
      if (!invoice.pdf_path) {
        await generateInvoicePdf(invoice.id);
      }
      await downloadAndSavePdf(invoice.id, invoice.invoice_number);
      // Refresh usage
      dispatch(fetchUsage());
    } catch (err) {
      toast.error(await parseBlobError(err, 'Failed to download the PDF.'));
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadXml = async (invoice: Invoice, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setDownloadingXml(invoice.id);
      await downloadAndSaveXml(invoice.id, invoice.invoice_number);
    } catch (err: any) {
      toast.error(await parseBlobError(err, 'Failed to generate KSeF XML.'));
    } finally {
      setDownloadingXml(null);
    }
  };

  const handleMarkSent = async (invoiceId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await dispatch(markInvoiceSent(invoiceId)).unwrap();
      toast.success('Marked as sent.');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to mark the invoice as sent.'));
    }
  };

  const handleMarkConfirmed = async (invoiceId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await dispatch(markInvoiceConfirmed(invoiceId)).unwrap();
      toast.success('Invoice confirmed.');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to confirm the invoice.'));
    }
  };

  const handleMarkPaid = async (invoiceId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await dispatch(markInvoicePaid(invoiceId)).unwrap();
      toast.success('Marked as paid.');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to mark the invoice as paid.'));
    }
  };

  // `usage === null` means the usage fetch has not landed (or failed) — it does
  // NOT mean the limit is reached. Treating it as "blocked" showed a full-page
  // "Creation Limit Reached" screen and a false "You have reached your PDF
  // download limit" banner to users who were nowhere near their limit. The
  // backend enforces the real limit on every request, so optimistically
  // allowing here is safe.
  const canCreate = usage ? usage.invoices_created < usage.invoices_created_limit : true;
  const canEdit = usage ? usage.invoices_edited < usage.invoices_edited_limit : true;
  const canDownload = usage ? usage.invoice_downloads < usage.invoice_downloads_limit : true;

  const renderInvoiceCard = (invoice: Invoice) => (
    <Card
      key={invoice.id}
      className="hover:bg-coral-500/10 transition-all duration-200 cursor-pointer relative group"
      onClick={() => navigate(`/created-invoices/${invoice.id}`)}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="p-2 rounded-lg bg-coral-500/10">
              <Receipt className="w-6 h-6 text-coral-400" />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base text-foreground truncate">
                {invoice.invoice_number}
              </CardTitle>
              <p className="text-sm text-foreground-muted">
                {invoice.client?.name || 'No client'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2">
            {/* Labelled by the money flow, not the document direction: an "IN"
                badge on a bill you owe read as money coming in. */}
            {invoice.direction === 'incoming' && (
              <div
                className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 whitespace-nowrap"
                title="Incoming invoice — a bill you pay, so it counts as money out"
              >
                MONEY OUT
              </div>
            )}
            <div className={`px-2 py-1 rounded-full text-xs ${getStatusColor(invoice.status)}`}>
              {getInvoiceStatusLabel(invoice.status, invoice.direction)}
            </div>
            {invoice.invoice_type !== 'proforma' && ksefStatusOf(invoice) !== 'none' && (
              <KsefChip status={ksefStatusOf(invoice)} size="sm" />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-sm mb-3">
          <div className="flex items-center gap-2 text-foreground-muted">
            <Calendar className="w-4 h-4" />
            {invoice.invoice_date ? formatDate(invoice.invoice_date) : 'No date'}
          </div>
          <div className="text-lg font-semibold text-foreground">
            {formatMoney(invoice.total, invoice.currency)}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 pt-3 border-t border-coral-500/10 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/created-invoices/${invoice.id}`); }}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-foreground-muted hover:bg-coral-500/20 rounded transition-colors"
            title="View"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          {(invoice.status === 'ready_to_process' || invoice.status === 'confirmed') && canEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/created-invoices/${invoice.id}/edit`); }}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-foreground-muted hover:bg-coral-500/20 rounded transition-colors"
              title="Edit"
            >
              <Edit className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={(e) => handleDownload(invoice, e)}
            disabled={downloading === invoice.id || !canDownload}
            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-foreground-muted hover:bg-coral-500/20 rounded transition-colors ${
              !canDownload ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={canDownload ? 'Download PDF' : 'Download limit reached'}
          >
            {downloading === invoice.id ? (
              <Loader className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
          </button>
          {invoice.invoice_type !== 'proforma' && (
            <button
              onClick={(e) => handleDownloadXml(invoice, e)}
              disabled={downloadingXml === invoice.id}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-foreground-muted hover:bg-coral-500/20 rounded transition-colors"
              title="Download KSeF XML"
            >
              {downloadingXml === invoice.id ? (
                <Loader className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FileText className="w-3.5 h-3.5" />
              )}
            </button>
          )}
          {invoice.invoice_type !== 'proforma' &&
            isKsefEligible &&
            ['none', 'rejected'].includes(ksefStatusOf(invoice)) && (
              <button
                onClick={(e) => { e.stopPropagation(); setKsefModalInvoice(invoice); }}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-foreground-muted hover:bg-coral-500/20 rounded transition-colors"
                title="Submit to KSeF"
              >
                <Landmark className="w-3.5 h-3.5" />
              </button>
            )}
          {invoice.status === 'ready_to_process' && (
            <button
              onClick={(e) => handleMarkConfirmed(invoice.id, e)}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-foreground-muted hover:bg-coral-500/20 rounded transition-colors"
              title="Confirm"
            >
              <CheckCircle className="w-3.5 h-3.5" />
            </button>
          )}
          {invoice.status === 'confirmed' && (
            <button
              onClick={(e) => handleMarkSent(invoice.id, e)}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-foreground-muted hover:bg-coral-500/20 rounded transition-colors"
              title="Mark as Sent"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          )}
          {(invoice.status === 'sent' || invoice.status === 'overdue') && (
            <button
              onClick={(e) => handleMarkPaid(invoice.id, e)}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-green-300 hover:bg-green-500/20 rounded transition-colors"
              title="Mark as Paid"
            >
              <Coins className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={(e) => handleDelete(invoice.id, e)}
            disabled={deleting === invoice.id}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-red-300 hover:bg-red-500/20 rounded transition-colors"
            title="Delete"
          >
            {deleting === invoice.id ? (
              <Loader className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      </CardContent>
    </Card>
  );

  const renderInvoiceRow = (invoice: Invoice) => (
    <div
      key={invoice.id}
      className="flex items-center gap-4 p-4 hover:bg-coral-500/10 transition-all duration-200 cursor-pointer rounded-lg group"
      onClick={() => navigate(`/created-invoices/${invoice.id}`)}
    >
      <div className="p-2 rounded-lg bg-coral-500/10">
        <Receipt className="w-6 h-6 text-coral-400" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-base text-foreground truncate">{invoice.invoice_number}</h3>
        <p className="text-sm text-foreground-secondary">{invoice.client?.name || 'No client'}</p>
      </div>
      <div className="text-sm text-foreground-secondary hidden md:block">
        {invoice.invoice_date ? formatDate(invoice.invoice_date) : '-'}
      </div>
      <div className="text-lg font-semibold text-foreground w-32 text-right">
        {formatMoney(invoice.total, invoice.currency)}
      </div>
      {invoice.direction === 'incoming' && (
        <div
          className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 whitespace-nowrap"
          title="Incoming invoice — a bill you pay, so it counts as money out"
        >
          MONEY OUT
        </div>
      )}
      <div className={`px-2 py-1 rounded-full text-xs ${getStatusColor(invoice.status)}`}>
        {getInvoiceStatusLabel(invoice.status, invoice.direction)}
      </div>
      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => handleDownload(invoice, e)}
          disabled={downloading === invoice.id || !canDownload}
          className={`p-1.5 text-foreground-muted hover:bg-coral-500/20 rounded transition-colors ${
            !canDownload ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          title="Download PDF"
        >
          {downloading === invoice.id ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
        </button>
        {invoice.invoice_type !== 'proforma' && (
          <button
            onClick={(e) => handleDownloadXml(invoice, e)}
            disabled={downloadingXml === invoice.id}
            className="p-1.5 text-foreground-muted hover:bg-coral-500/20 rounded transition-colors"
            title="Download KSeF XML"
          >
            {downloadingXml === invoice.id ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
          </button>
        )}
        <button
          onClick={(e) => handleDelete(invoice.id, e)}
          disabled={deleting === invoice.id}
          className="p-1.5 text-foreground-muted hover:text-red-400 hover:bg-red-500/20 rounded transition-colors"
          title="Delete"
        >
          {deleting === invoice.id ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="inv-container">
      {/* Header */}
      <div className="inv-header">
        <div>
          <div className="inv-eyebrow"><span className="dot" />Billing · create, send, get paid</div>
          <h1>Created Invoices</h1>
          <p className="sub">Invoices you issue from ViewPoint — from draft to paid.</p>
        </div>
        <div className="inv-header-actions">
          <button
            onClick={() => navigate('/created-invoices/new')}
            disabled={!canCreate}
            className="inv-btn primary"
            title={canCreate ? 'Create new invoice' : 'Invoice creation limit reached'}
          >
            <Plus />
            <span className="hidden sm:inline">New Invoice</span>
          </button>
          <button
            onClick={() => navigate('/ai', {
              state: {
                prefillMessage: 'Summarise my created invoices: total billed, how much is paid vs unpaid, and anything overdue or needing attention.',
                source: 'created_invoices',
              },
            })}
            className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-lg text-sm font-medium text-purple-600 dark:text-purple-300 border border-purple-500/25 hover:bg-purple-500/10 transition-colors"
            title="Ask the AI assistant about your invoices"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Ask AI</span>
          </button>
          <button
            onClick={() => navigate('/created-invoices/settings')}
            className="inv-btn icon-btn"
            title="Invoice Settings"
          >
            <Settings />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`inv-btn icon-btn${viewMode === 'grid' ? ' active' : ''}`}
            title="Grid view"
          >
            <Grid />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`inv-btn icon-btn${viewMode === 'list' ? ' active' : ''}`}
            title="List view"
          >
            <List />
          </button>
        </div>
      </div>

      {/* Usage bars */}
      {usage && (
        <div className="inv-panel">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <UsageBar
              label="Invoices Created"
              used={usage.invoices_created}
              limit={usage.invoices_created_limit}
              icon={<Receipt className="w-4 h-4" />}
            />
            <UsageBar
              label="Invoices Edited"
              used={usage.invoices_edited}
              limit={usage.invoices_edited_limit}
              icon={<Edit className="w-4 h-4" />}
            />
            <UsageBar
              label="PDF Downloads"
              used={usage.invoice_downloads}
              limit={usage.invoice_downloads_limit}
              icon={<Download className="w-4 h-4" />}
            />
          </div>
          {!canCreate && (
            <div className="mt-3 flex items-center gap-2 text-sm text-orange-300">
              <AlertTriangle className="w-4 h-4" />
              <span>Invoice creation limit reached.</span>
              <button
                onClick={() => navigate('/pricing')}
                className="text-coral-400 hover:text-foreground underline"
              >
                Upgrade your plan
              </button>
            </div>
          )}
        </div>
      )}

      {/* Search and filters */}
      <div className="inv-toolbar">
        <div className="inv-search">
          <Search />
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`inv-filter-toggle${showFilters ? ' active' : ''}`}
        >
          <Filter />
          Filters
        </button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="inv-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="inv-field">
              <label>Status</label>
              <select
                value={statusFilter || ''}
                onChange={(e) =>
                  dispatch(setStatusFilter((e.target.value as InvoiceStatus) || null))
                }
              >
                <option value="">All Statuses</option>
                <option value="ready_to_process">Ready to Process / Received</option>
                <option value="confirmed">Confirmed / Approved to Pay</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="inv-field">
              <label>Client</label>
              <select
                value={clientFilter || ''}
                onChange={(e) =>
                  dispatch(setClientFilter(e.target.value ? parseInt(e.target.value) : null))
                }
              >
                <option value="">All Clients</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="inv-alert">
          <AlertTriangle />
          <span>{error}</span>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="inv-loading">
          <div className="inv-spinner" />
        </div>
      )}

      {/* Empty state */}
      {!loading && filteredInvoices.length === 0 && (
        <div className="inv-empty">
          <div className="inv-empty-icon">
            <Receipt />
          </div>
          <h3>No invoices yet</h3>
          <p>Create your first invoice to get started</p>
          {canCreate && (
            <button onClick={() => navigate('/created-invoices/new')} className="inv-btn primary">
              Create Invoice
            </button>
          )}
        </div>
      )}

      {/* Invoice grid/list */}
      {!loading && filteredInvoices.length > 0 && (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredInvoices.map(renderInvoiceCard)}
          </div>
        ) : (
          <div className="inv-list">
            {filteredInvoices.map(renderInvoiceRow)}
          </div>
        )
      )}

      {ksefModalInvoice && (
        <KsefSubmitModal
          invoiceId={ksefModalInvoice.id}
          invoiceNumber={ksefModalInvoice.invoice_number}
          buyerName={ksefModalInvoice.client?.name}
          total={formatMoney(ksefModalInvoice.total, ksefModalInvoice.currency)}
          environment={resolveKsefEnv(ksefModalInvoice)}
          onClose={() => setKsefModalInvoice(null)}
          onDone={(ksef) =>
            setKsefStateById((m) => ({ ...m, [ksefModalInvoice.id]: ksef }))
          }
        />
      )}
    </div>
  );
};

// Keep backward compatibility with old name
export { CreatedInvoicesPage as InvoicesPage };
export default CreatedInvoicesPage;
